import { useEffect, useState } from "react";
import type { Member, WsServerMessage } from "../shared/contracts";

export interface RoomState {
  members: Member[];
  connected: boolean;
  roomName: string | null;
  generation: number; // increments on each server init — use as key on VotingBoard
  hasInit: boolean; // true after first init message received
}

export function useRoom(code: string | undefined): RoomState {
  const [state, setState] = useState<RoomState>({
    members: [],
    connected: false,
    roomName: null,
    generation: 0,
    hasInit: false,
  });

  useEffect(() => {
    if (!code) return;

    let ws: WebSocket;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    function connect() {
      if (cancelled) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${protocol}//${window.location.host}/ws?room=${encodeURIComponent(code!)}`);

      ws.onopen = () => setState(s => ({ ...s, connected: true }));

      ws.onerror = () => setState(s => ({ ...s, connected: false }));

      ws.onmessage = (event) => {
        try {
          const msg: WsServerMessage = JSON.parse(event.data);
          if (msg.type === "init") {
            setState(s => ({
              ...s,
              members: msg.room.members,
              roomName: msg.room.name,
              generation: s.generation + 1,
              hasInit: true,
            }));
          } else if (msg.type === "update") {
            setState(s => ({ ...s, members: msg.members }));
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onclose = () => {
        setState(s => ({ ...s, connected: false }));
        if (!cancelled) {
          retryTimeout = setTimeout(connect, 3000);
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      ws?.close();
    };
  }, [code]);

  return state;
}
